import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import { EventDetailPairingItem } from "./EventDetailPairingItem";
import { EventDetailPairings_event$key } from "./__generated__/EventDetailPairings_event.graphql";

type EventDetailFooterProps = {
  fragmentRef: EventDetailPairings_event$key;
};

export const EventDetailPairings = ({
  fragmentRef,
}: EventDetailFooterProps) => {
  const data = useFragment(EventDetailPairingsFragment, fragmentRef);
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const currentProfile = useAtomValue(currentProfileAtom);
  const currentMemberId = currentProfile?.memberId;

  const currentEventSignup = useMemo(() => {
    return data?.eventSignUps?.edges?.find((s) =>
      s?.node?.registrations?.edges?.find(
        (r) => r?.node?.registrant?.id === currentMemberId
      )
    )?.node;
  }, [data, currentMemberId]);

  const hasNoPairings =
    !currentEventSignup ||
    data === null ||
    !currentEventSignup?.bookings?.edges ||
    currentEventSignup?.bookings?.edges?.length === 0;
  if (hasNoPairings) {
    return null;
  }

  const pairings = currentEventSignup?.bookings?.edges?.map(
    (bookingEdge, index) => {
      const booking = bookingEdge?.node;

      return booking ? (
        <EventDetailPairingItem
          key={`host-${booking?.id}`}
          fragmentRef={booking}
        />
      ) : null;
    }
  );

  const eventSession = data?.firstEventSession?.edges?.[0]?.node ?? null;
  const agendaDate = eventSession?.agenda?.date;
  const facilitySlug = eventSession?.agenda?.facility?.slug;
  const startHour = eventSession?.startAt
    ? Number.parseInt(eventSession.startAt.slice(0, 2))
    : undefined;

  return pairings ? (
    <VStack mt={6}>
      <Text
        variant="mainSmallSemiBold"
        lineHeight={32}
        testID="pairings-heading"
      >{t`Pairings`}</Text>
      <Box marginBottom={4} overflow="hidden">
        {pairings}
      </Box>
      <Button
        variant="secondary"
        onPress={() => {
          navigation.navigate("Agenda", {
            facilitySlug: facilitySlug ?? "",
            date: agendaDate ?? "",
            startHour,
          });
        }}
        testID="view-all-pairings-button"
        accessibilityLabel={t`View all pairings button`}
      >
        {t`View all pairings`}
      </Button>
    </VStack>
  ) : null;
};

export const EventDetailPairingsFragment = graphql`
  fragment EventDetailPairings_event on Event {
    id
    firstEventSession: eventSessions(first: 1) {
      edges {
        node {
          startAt
          agenda {
            date
            facility {
              slug
            }
          }
        }
      }
    }
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
          registrations {
            edges {
              node {
                registrant {
                  id
                }
              }
            }
          }
          bookings {
            edges {
              node {
                id
                ...EventDetailPairingItem_booking
              }
            }
          }
          ...EventDetailAttendeesListItemFragment
          ...EventDetailAttendeesGroupListItemFragment
        }
      }
    }
  }
`;
